import { InsightsScope } from "hooks/use-insights-scope";
import { ALL_WORKSPACES_ID } from "v2/services/insights.service";

/**
 * Decisões do painel sobre a lista de contratos, fora do componente para
 * serem testáveis sem montar o painel inteiro.
 */

export interface IListingStatus {
  /** Com escopo, `/organizations/{cliente}/workspaces` falhou (corretora). */
  scopedFailed: boolean;
  /** A lista pesquisa os fornecedores do menu (RH, ou rota `/insights`). */
  viaSellers: boolean;
  /** `/organizations/list` (os fornecedores do menu) falhou. */
  sellersFailed: boolean;
  /** Alguma `/organizations/{fornecedor}/workspaces` da pesquisa falhou. */
  sweepFailed: boolean;
  /** Quantos contratos chegaram, já recortados pelo escopo. */
  count: number;
}

/**
 * O que a falha na listagem significa para a tela.
 *
 * - `"falhou"`: nada chegou e algo falhou. Nunca vira "sem contrato": a
 *   falta de lista não é prova de que o cliente não tem contrato.
 * - `"parcial"`: chegaram contratos, mas algum fornecedor falhou; os contratos
 *   dele não estão na lista e o painel tem de dizer isso.
 * - `null`: a lista é a verdade (inclusive vazia).
 */
export function listingProblem(
  status: IListingStatus
): "falhou" | "parcial" | null {
  if (!status.viaSellers) return status.scopedFailed ? "falhou" : null;
  if (!status.sellersFailed && !status.sweepFailed) return null;
  return status.count === 0 ? "falhou" : "parcial";
}

/**
 * Se o seletor oferece "Todos os contratos".
 *
 * Só no assistente (o backend recusa `ALL` nos relatórios), só para usuário de
 * corretora (`user.isSeller`, o mesmo critério de `insightsScopeFor`) e nunca
 * com escopo da própria organização: `ALL` não recorta por contrato nem por
 * CNPJ, lê a conta Snowflake inteira, e há contas partilhadas entre um cliente
 * real e organizações de demonstração. Vale também para a rota `/insights` sem
 * escopo, aberta a qualquer usuário logado; o backend recusa `ALL` (403) a quem
 * não é corretora desde a #2086.
 */
export function allowsAllContracts(
  tab: "assistente" | "relatorios",
  scope: InsightsScope | null | undefined,
  isSeller: boolean
): boolean {
  return tab === "assistente" && isSeller && !scope?.isOwnOrganization;
}

/**
 * O contrato que o painel deve escolher sozinho, ou `null` para manter o atual.
 *
 * Enquanto a lista carrega, não troca nada: numa pesquisa por fornecedores a
 * lista chega aos poucos, e o contrato da tela (pré-selecionado) pode estar no
 * fornecedor que ainda não respondeu. Trocar antes descartaria a escolha.
 */
export function autoSelectedWorkspace(params: {
  workspaceIds: string[];
  current: string;
  isLoading: boolean;
  allowAll: boolean;
}): string | null {
  const { workspaceIds, current, isLoading, allowAll } = params;
  if (isLoading || workspaceIds.length === 0) return null;
  const isAllSelectable =
    allowAll && workspaceIds.length > 1 && current === ALL_WORKSPACES_ID;
  if (isAllSelectable || workspaceIds.includes(current)) return null;
  return workspaceIds[0];
}
